import { NgClass } from '@angular/common';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, effect, ElementRef, inject, Injector, input, model, output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { POSITION_ACTIVITY_OPTIONS } from '../../../../shared/constants/order-document-labels';
import { SelectOnFocusDirective } from '../../../../shared/directives/select-on-focus.directive';
import { TimePickerModalComponent } from '../../../../shared/modals/time-picker-modal/time-picker-modal.component';
import { IOrderDocumentPosition } from '../../../../shared/models/order-document.model';
import { ModalService } from '../../../../shared/services/modal.service';

// Builds one Zeiterfassung row. Exported because the page builds the rows of a loaded document
// itself (see CreateOrderDocumentComponent.patchFormValues). View-only roles (e.g. Wareneingang)
// may see rows in edit mode but not edit them — rows are built with their controls already
// disabled so the page's enable/disable effect doesn't have to catch up.
export function createPositionGroup(fb: FormBuilder, position: IOrderDocumentPosition | undefined, disabled: boolean): FormGroup {
  return fb.group({
    _id: [position?._id ?? null],
    posNr: [{ value: position?.posNr ?? '', disabled }, Validators.required],
    date: [{ value: position?.date ? new Date(position.date) : new Date(), disabled }, Validators.required],
    // Not prefilled from the logged-in user: many employees share the same account, so each
    // Positionen row must have its own Pers.-Nr. typed in by hand. Loaded positions keep their
    // stored value.
    employeeNumber: [{ value: position?.employeeNumber ?? '', disabled }, Validators.required],
    activity: [{ value: position?.activity ?? null, disabled }, Validators.required],
    stueck: [{ value: position?.stueck ?? null, disabled }],
    time: [{ value: position?.time ?? '', disabled }, Validators.required],
    bemerkung: [{ value: position?.bemerkung ?? '', disabled }],
  });
}

// The Zeiterfassung (positions) table of an Auftragsbegleitschein. Edits the page's `positions`
// FormArray in place — loading, pruning and saving the rows stays with the page.
@Component({
  selector: 'app-positions-table',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, DatePickerModule, SelectModule, SelectOnFocusDirective],
  templateUrl: './positions-table.component.html',
  styleUrls: ['./positions-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class PositionsTableComponent {
  private readonly fb = inject(FormBuilder);
  private readonly modalService = inject(ModalService);
  private readonly elementRef = inject(ElementRef);
  private readonly injector = inject(Injector);

  public readonly positions = input.required<FormArray>();
  // The document exists (edit mode). Before that there's nothing to record yet.
  public readonly saved = input(false);
  // A Kunde is assigned. Times can only be recorded for a document with a Kunde.
  public readonly hasCustomer = input(false);
  // Rows can be added, removed and edited (saved document with a Kunde)
  public readonly editable = input(false);
  // Shows "Fehler erfassen" on each row (ADMIN and ENDKONTROLLE only)
  public readonly canRecordFalsePosition = input(false);
  public readonly isSubmitting = input(false);
  // "Kopfzeile fixieren". Two-way, so the page can release it when switching to another tab.
  public readonly headerPinned = model(false);

  // "Fehler erfassen" on a row — the page copies the row into a new Fehlererfassung row.
  public readonly recordFalsePosition = output<IOrderDocumentPosition>();

  public readonly positionActivityOptions = POSITION_ACTIVITY_OPTIONS;

  constructor() {
    // The pinned Kopfzeile locks <main>'s scrolling so only the table scrolls underneath it.
    effect(() => {
      const main = document.querySelector('main') as HTMLElement | null;
      if (main) {
        main.style.overflow = this.headerPinned() ? 'hidden' : '';
      }
    });

    // Undo that lock so it doesn't leak into other pages if we navigate away while pinned.
    inject(DestroyRef).onDestroy(() => {
      const main = document.querySelector('main') as HTMLElement | null;
      if (main) {
        main.style.overflow = '';
      }
    });
  }

  // Typed for [formGroup] under strictTemplates (FormArray.controls is AbstractControl[]).
  public get rows(): FormGroup[] {
    return this.positions().controls as FormGroup[];
  }

  public toggleHeaderPin(): void {
    this.headerPinned.update(pinned => !pinned);
    if (this.headerPinned()) {
      const wrapper = (this.elementRef.nativeElement as HTMLElement).querySelector('.table-wrapper');
      wrapper?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  public addRow(): void {
    if (!this.editable()) return;
    this.positions().push(createPositionGroup(this.fb, undefined, false));

    // The new row doesn't exist in the DOM yet — focus its Pos. once it has rendered, so the user can
    // type right away.
    const index = this.positions().length - 1;
    afterNextRender(
      () => {
        const row = (this.elementRef.nativeElement as HTMLElement).querySelectorAll<HTMLElement>('.position-row')[index];
        row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        row?.querySelector<HTMLElement>('[formcontrolname="posNr"]')?.focus({ preventScroll: true });
      },
      { injector: this.injector }
    );
  }

  public removeRow(index: number): void {
    if (!this.editable()) return;
    this.positions().removeAt(index);
    // removeAt() alone doesn't mark the form dirty — without this, unsavedChangesGuard wouldn't warn.
    this.positions().markAsDirty();
  }

  public onRecordFalsePosition(row: FormGroup): void {
    this.recordFalsePosition.emit(row.getRawValue() as IOrderDocumentPosition);
  }

  public openTimePicker(row: FormGroup): void {
    const time = row.get('time');
    this.modalService
      .open(TimePickerModalComponent, { initial: time?.value })
      .then((result: string | null) => {
        if (result) {
          time?.setValue(result);
          // setValue() doesn't mark the control dirty — without this, unsavedChangesGuard wouldn't warn
          // and the page's pruneUntouchedNewRows() would drop a row whose only input is the picked time.
          time?.markAsDirty();
          time?.markAsTouched();
        }
        this.modalService.close();
      })
      .catch(() => { });
  }

  public isFieldInvalid(control: AbstractControl | null): boolean {
    return !!control && control.invalid && control.touched;
  }
}
